import React from 'react';
import ReactDOM from 'react-dom/client';
import StrFormatter from '../../../app/formatters/StrFormatter';

/**
 * @typedef {object} GwSelectOptionItem
 * @property {string} key - Clés de l'option d'item
 * @property {string} name - Nom à afficher visuellement de l'option d'item
 * @property {string} value - Valeur de l'option d'item
 * @property {boolean} [inFirst] - Si l'option d'item doit être en premier dans la liste
 */

/**
 * @typedef {object} GwSelectStateItem
 * @property {Array<GwSelectOptionItem>} options - Liste des options pour remplir la liste
 * @property {Array<GwSelectOptionItem>} filteredOptions - Liste des options filtrer pour remplir la liste
 * @property {string} inputOutputValue - Valeur du champ à fournir lors du post
 * @property {string} inputNameToSend - Nom du champ en envoyer lors du post
 * @property {string} inputPlaceholder - Libellé du placeholder du champ de recherche
 * @property {string} apiUrl - Lien de l'api pour récupérer la liste
 * @property {string} apiProperties - Liste des propriétés de l'api à afficher dans la liste
 * @property {string} apiParams - Liste des paramètre en plus à fournir pour l'api
 * @property {boolean} autofocus - Si on doit rajouter l'autofocus sur le champ par défaut
 * @property {string} valueIsHref - True si lors du clic sur un item on se dirrige vers la valeur de l'item qui est une URL
 * @property {boolean} submitFormOnSelect - True si lors du clic sur un item on soumet le formulaire
 * @property {string} value - Valeur de la liste déroulante
 * @property {boolean} showMenu - Si on affiche le menu ou pas en fonction du focus et blue
 * @property {string} search - Chaîne de la recherche courante dans la liste
 * @property {boolean} required - Si le champ est obligatoire ou pas
 * @property {boolean} dataIsLoaded - Si les données sont chargées ou pas
 * @property {GwSelectOptionItem} selectedOption - Option sélectionné dans la liste
 * @property {number} selectedItemIndex - Index de l'item sélectionné dans la liste
 */

class GwSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = /**@type {GwSelectStateItem}*/ ({
      inputOutputValue: props.inputOutputValue || '',
      inputNameToSend: props.inputNameToSend,
      apiUrl: props.apiUrl,
      apiProperties: props.apiProperties,
      apiParams: props.apiParams,
      valueIsHref: props.valueIsHref,
      autofocus: props.autofocus === 'true',
      submitFormOnSelect: props.submitFormOnSelect === 'true',
      inputPlaceholder: props.inputPlaceholder || 'Votre recherche',
      options: [],
      filteredOptions: [],
      value: '',
      showMenu: false,
      search: '',
      selectedOption: /**@type {GwSelectOptionItem}*/ ({}),
      selectedItemIndex: 0,
      required: props.required === 'true' || false,
      dataIsLoaded: false
    });

    this.selectInput = React.createRef();
    this.selectMenu = React.createRef();
    this.selectMenuLis = [];
  }

  componentDidMount() {
    var findedOption;

    this.loadDatas().then(options => {
      if (this.state.inputOutputValue) {
        findedOption = this.findOption(options, 'value', this.state.inputOutputValue);

        this.setState({
          options,
          filteredOptions: options,
          dataIsLoaded: true,
          value: findedOption.name,
          selectedOption: findedOption
        });
      } else {
        this.setState({
          options,
          filteredOptions: options,
          dataIsLoaded: true
        });
      }
    });
  }

  /**
   * Lors du clic sur un item dans la liste
   * @param {GwSelectOptionItem} option Option associé au clic sur l'item de liste
   */
  selectItem(option) {
    var formNode, submitNode, event;

    if (option) {
      this.setState(
        {
          inputOutputValue: option.key === 'noValues' ? '' : option.value,
          value: option.key === 'noValues' ? '' : option.name,
          showMenu: false,
          selectedOption: option.key === 'noValues' ? {} : option
        },
        () => {
          if (this.state.valueIsHref) {
            document.location.href = option.value;
          }

          if (this.state.submitFormOnSelect) {
            formNode = this.selectInput.current.closest('form');
            submitNode = formNode.querySelector('[type="submit"]');
            event = new Event('click');
            submitNode.dispatchEvent(event);
          }
        }
      );
    } else {
      this.setState({
        inputOutputValue: '',
        value: '',
        showMenu: false,
        selectedOption: {}
      });
    }
  }

  /**
   * Lors du changement de valeur dans le champ de recherche
   * @param {PointerEvent} e Evénement associé au changement de valeur dans le champ de recherche
   */
  onChangeOnSearch(e) {
    var searchValue = /** @type {HTMLInputElement}*/(e.target).value,
      filteredOptions,
      emptyOptionFinded,
      formattedSearch,
      formattedOptionName,
      searchWords;

    filteredOptions = this.state.options.filter(option => {
      formattedOptionName = StrFormatter.replaceAccents(option.name.toLowerCase());
      formattedSearch = StrFormatter.replaceAccents(this.state.search.toLowerCase());
      searchWords = formattedSearch.split(' ');
      return searchWords.every(word => formattedOptionName.includes(word));
    });

    emptyOptionFinded = filteredOptions.find(option => option.key === 'noValues');

    if (!this.state.required && !this.state.valueIsHref && !emptyOptionFinded || filteredOptions.length === 0) {
      filteredOptions.unshift({
        key: 'noValues',
        name: 'Aucune valeur',
        value: ''
      });
    }

    this.setState({
      search: searchValue,
      value: searchValue,
      selectedItemIndex: 0,
      filteredOptions,
      showMenu: true
    });
  }

  /**
   * Lors du key up pour gérer la flèche du haut ou la flèche du bas
   * @param {KeyboardEvent} e - Événement associé à l'utilisation des touches clavier
   */
  onKeyUp(e) {
    var selectedItemIndex = this.state.selectedItemIndex,
      firstIndex = 0,
      lastIndex = this.state.filteredOptions.length - 1,
      currentSelectedLiNode;

    if (!this.state.dataIsLoaded) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        selectedItemIndex = selectedItemIndex === lastIndex ? firstIndex : selectedItemIndex + 1;
        break;
      case 'ArrowUp':
        selectedItemIndex = selectedItemIndex === 0 ? lastIndex : selectedItemIndex - 1;
        break;
    }

    currentSelectedLiNode = this.selectMenuLis[selectedItemIndex].current;

    this.selectMenu.current.scrollTo({
      top: currentSelectedLiNode.offsetTop
    });

    this.setState({
      selectedItemIndex
    });
  }

  /**
   * Lors du key down pour gérer la tocuhe entrée pour sélectionner une valeur
   * @param {KeyboardEvent} e - Événement associé à l'utilisation des touches clavier
   */
  onKeyDown(e) {
    var selectedItemIndex = this.state.selectedItemIndex;

    if (!this.state.dataIsLoaded) {
      return;
    }

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        this.selectItem(this.state.filteredOptions[selectedItemIndex]);
        break;
    }
  }

  /**
   * Lors du blur dans le champ de recherche
   * @param {PointerEvent} e Evénement associé au blur dans le champ de recherche
   */
  onBlur(e) {
    var search = this.state.search,
      findedOptionWithSearch = this.findOption(this.state.options, 'name', search);

    this.setState({
      showMenu: false,
      filteredOptions: this.state.options
    });

    if (!this.state.inputOutputValue) {
      this.setState({
        value: '',
        inputOutputValue: ''
      });
    }

    if (!findedOptionWithSearch) {
      this.setState({
        value: this.state.selectedOption?.name || ''
      });
    }
  }

  findOption(options, propertyToFind, value) {
    switch (propertyToFind) {
      case 'name':
        return options.find(option => option.name === value);
        break;
      case 'value':
        return options.find(option => option.value === parseInt(value, 10));
        break;
    }
  }

  /**
   * Lors du focus dans le champ de recherche
   * @param {PointerEvent} e Evénement associé au focus dans le champ de recherche
   */
  onFocus(e) {
    this.setState({
      showMenu: this.state.autofocus ? false : true
    });

    if (this.selectInput.current) {
      this.selectInput.current.select();
    }

    this.setState({
      search: ''
    });
  }

  /**
   * Lors du clic sur la flèche pour afficher la liste
   */
  onClickOnHideMenu(e) {
    this.setState({
      showMenu: false
    });
  }

  loadDatas() {
    var url, apiParams, options;

    if (this.state.dataIsLoaded) {
      return Promise.resolve(this.state.options);
    }

    url = this.state.apiUrl;
    apiParams = this.state.apiParams;

    if (apiParams) {
      url += `?${apiParams}`;
    }

    return fetch(url, {
      headers: {
        Authorization: 'gestiaweb'
      }
    })
      .then(res => res.json())
      .then(result => {
        options = this.state.apiProperties
          ? result.items.map((item, index) => {
              var nameArray = this.state.apiProperties.split(',').reduce((arrayStr, property) => {
                if (item[property]) {
                  arrayStr.push(item[property]);
                }
                return arrayStr;
              }, []);

              return {
                name: nameArray.join(' '),
                value: item.id,
                key: index
              };
            })
          : result.options;
        
        options = options.map(option => {
          if (!option.hasOwnProperty('inFirst')) {
            option.inFirst = false;
          }
          return option;
        });

        options = _.orderBy(options, ['inFirst', 'name'], ['desc', 'asc']);

        if (!this.state.required && !this.state.valueIsHref) {
          options.unshift({
            key: 'noValues',
            inFirst: true,
            name: 'Aucune valeur',
            value: ''
          });
        }

        return Promise.resolve(options);
      });
  }

  /**
   * Lors du render du composant
   * @returns Rendu HTML
   */
  render() {
    var options = this.state.filteredOptions,
      menuStyle,
      expandMoreIconStyle,
      expandLessIconStyle,
      selectLoaderStyle;

    if (this.state.showMenu) {
      menuStyle = { display: 'block' };
      expandMoreIconStyle = { display: 'none' };
      expandLessIconStyle = { display: 'flex' };
    } else {
      menuStyle = { display: 'none' };
      expandMoreIconStyle = { display: 'flex' };
      expandLessIconStyle = { display: 'none' };
    }

    selectLoaderStyle = this.state.dataIsLoaded ? { display: 'none' } : { display: 'block' };

    options.forEach((option, index) => {
      this.selectMenuLis[index] = React.createRef();
    });

    return (
      <div className="select">
        <input type="hidden" value={this.state.inputOutputValue} name={this.state.inputNameToSend} />
        <input data-type="text" data-required={this.state.required} type="text" ref={this.selectInput} className="select__input" aria-label="Votre recherche" value={this.state.value} placeholder={this.state.inputPlaceholder} onFocus={this.onFocus.bind(this)} autoFocus={this.state.autofocus} onBlur={this.onBlur.bind(this)} onChange={this.onChangeOnSearch.bind(this)} onKeyUp={this.onKeyUp.bind(this)} onKeyDown={this.onKeyDown.bind(this)} />
        <span className="select__icon material-icons" style={expandMoreIconStyle} onClick={() => this.selectInput.current.focus()}>
          expand_more
        </span>
        <span className="select__icon material-icons" style={expandLessIconStyle} onClick={this.onClickOnHideMenu.bind(this)}>
          expand_less
        </span>
        <div className="select__menu" style={menuStyle} ref={this.selectMenu}>
          {options.length === 0 && this.state.dataIsLoaded && <p className="select__no-items">Aucune valeur</p>}
          <img className="select__loader" src="/images/icons/loader.svg" style={selectLoaderStyle} role="presentation" />
          {options.length > 0 && (
            <ul className="select__menu-ul">
              {options.map((option, index) => (
                <li ref={this.selectMenuLis[index]} className={this.state.selectedItemIndex === index ? 'select__menu-li select__menu-li--selected' : 'select__menu-li'} key={option.key} onMouseDown={this.selectItem.bind(this, option)}>
                  {option.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }
}

var selectNodes = document.getElementsByClassName('gw-select');
Array.prototype.forEach.call(selectNodes, function (selectNode) {
  var root = ReactDOM.createRoot(selectNode);
  root.render(<GwSelect apiParams={selectNode.getAttribute('data-api-params')} inputNameToSend={selectNode.getAttribute('data-input-name-to-send')} inputOutputValue={selectNode.getAttribute('data-input-output-value')} inputOutputValueToShow={selectNode.getAttribute('data-input-output-value-to-show')} apiUrl={selectNode.getAttribute('data-api-url')} apiProperties={selectNode.getAttribute('data-api-properties')} inputPlaceholder={selectNode.getAttribute('data-placeholder')} valueIsHref={selectNode.getAttribute('data-value-is-href')} submitFormOnSelect={selectNode.getAttribute('data-submit-form-on-select')} autofocus={selectNode.getAttribute('data-autofocus')} required={selectNode.getAttribute('data-required')} />);
});
